/**
 * Collections API - Core CRUD Operations
 *
 * List, Get, Create, Update, Delete collection endpoints.
 */

import { z } from 'zod'
import { PaginationMetaSchema } from '../common'
import {
  CollectionSchema,
  CollectionTypeSchema,
  CollectionVisibilitySchema,
  EmbedLayoutSchema,
  MediaTypeSchema
} from './common'

// ============================================================================
// GET /v1/collections (List)
// ============================================================================

export const ListCollectionsRequestSchema = z.object({
  limit: z.coerce.number().positive().max(100).optional(),
  offset: z.coerce.number().min(0).optional(),
  schoolId: z.string().optional(),
  userId: z.string().optional(),
  createdByUserId: z.string().optional(),
  collectionType: CollectionTypeSchema.optional(),
  visibility: CollectionVisibilitySchema.optional(),
  sourceGroupId: z.string().optional(),
  sourceLeadSectionId: z.string().optional(),
  searchQuery: z.string().optional(),
  orderBy: z.enum(['name', 'createdAt', 'updatedAt', 'mediaCount']).optional(),
  orderDirection: z.enum(['asc', 'desc']).optional(),
  includeDeleted: z.coerce.boolean().optional(),
  includeCounts: z.coerce.boolean().optional()
})

export type ListCollectionsRequest = z.infer<typeof ListCollectionsRequestSchema>

export const ListCollectionsResponseSchema = z.object({
  data: z.array(CollectionSchema),
  message: z.string(),
  meta: PaginationMetaSchema
})

export type ListCollectionsResponse = z.infer<typeof ListCollectionsResponseSchema>

// ============================================================================
// GET /v1/collections/:id (Get Single)
// ============================================================================

export const GetCollectionRequestSchema = z.object({
  id: z.string(),
  includeCounts: z.coerce.boolean().optional()
})

export type GetCollectionRequest = z.infer<typeof GetCollectionRequestSchema>

export const GetCollectionResponseSchema = z.object({
  data: CollectionSchema,
  message: z.string()
})

export type GetCollectionResponse = z.infer<typeof GetCollectionResponseSchema>

// ============================================================================
// POST /v1/collections (Create)
// ============================================================================

export const CreateCollectionRequestSchema = z.object({
  name: z.string().min(1).max(500),
  collectionType: CollectionTypeSchema,
  description: z.string().max(5000).optional().nullable(),
  coverMediaId: z.string().optional().nullable(),
  sourceGroupId: z.string().optional().nullable(),
  sourceLeadSectionId: z.string().optional().nullable(),
  sourceEventId: z.string().optional().nullable(),
  visibility: CollectionVisibilitySchema.optional(),
  allowMemberUploads: z.boolean().optional(),
  allowMemberComments: z.boolean().optional(),
  requireApproval: z.boolean().optional(),
  allowedMediaTypes: z.array(MediaTypeSchema).optional().nullable(),
  allowEmbedding: z.boolean().optional(),
  embedLayout: EmbedLayoutSchema.optional(),
  embedThemeColor: z
    .string()
    .regex(/^#[0-9A-Fa-f]{6}$/)
    .optional()
    .nullable(),
  embedShowMetadata: z.boolean().optional()
})

export type CreateCollectionRequest = z.infer<typeof CreateCollectionRequestSchema>

export const CreateCollectionResponseSchema = z.object({
  data: CollectionSchema,
  message: z.string()
})

export type CreateCollectionResponse = z.infer<typeof CreateCollectionResponseSchema>

// ============================================================================
// PATCH /v1/collections/:id (Update)
// ============================================================================

export const UpdateCollectionRequestSchema = z.object({
  name: z.string().min(1).max(500).optional(),
  description: z.string().max(5000).optional().nullable(),
  coverMediaId: z.string().optional().nullable(),
  visibility: CollectionVisibilitySchema.optional(),
  allowMemberUploads: z.boolean().optional(),
  allowMemberComments: z.boolean().optional(),
  requireApproval: z.boolean().optional(),
  allowedMediaTypes: z.array(MediaTypeSchema).optional().nullable(),
  allowEmbedding: z.boolean().optional(),
  embedLayout: EmbedLayoutSchema.optional(),
  embedThemeColor: z
    .string()
    .regex(/^#[0-9A-Fa-f]{6}$/)
    .optional()
    .nullable(),
  embedShowMetadata: z.boolean().optional()
})

export type UpdateCollectionRequest = z.infer<typeof UpdateCollectionRequestSchema>

export const UpdateCollectionResponseSchema = z.object({
  data: CollectionSchema,
  message: z.string()
})

export type UpdateCollectionResponse = z.infer<typeof UpdateCollectionResponseSchema>

// ============================================================================
// DELETE /v1/collections/:id (Delete)
// ============================================================================

export const DeleteCollectionRequestSchema = z.object({
  id: z.string()
})

export type DeleteCollectionRequest = z.infer<typeof DeleteCollectionRequestSchema>

export const DeleteCollectionResponseSchema = z.object({
  data: z.object({ success: z.boolean() }),
  message: z.string()
})

export type DeleteCollectionResponse = z.infer<typeof DeleteCollectionResponseSchema>
